import { useEffect, useState, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from 'antd';
import styled from 'styled-components';
import { formatAny } from '../../../utils/format';
import { parseAny } from '../../../utils/parse';
import { isSameRange, isValidRange } from '../../../utils/range';
import { useDebounce } from '../../../hooks/useDebounce';

type Range = [number, number];

type Props = {
  type: 'time' | 'duration' | 'distance' | 'count';
  range: Range | null;
  min: number;
  max: number;
  onDetermine?: (range: Range | null) => void;
};

const TIME_REPLACER = /[^\d:]/g;
const DURATION_REPLACER = /[^\dhm]/g;
const DISTANCE_REPLACER = /[^\dkm.]/g;
const COUNT_REPLACER = /[^\d]/g;

const HistogramFilterInput = ({
  range,
  type,
  min,
  max,
  onDetermine,
}: Props): ReactNode => {
  const { debounce: debounce1, cancel: cancel1 } = useDebounce(1000);
  const { debounce: debounce2, cancel: cancel2 } = useDebounce(1000);
  const { t } = useTranslation();

  const [minValue, setMinValue] = useState<string>((): string => {
    return range ? formatAny(type, range[0]) : '';
  });
  const [maxValue, setMaxValue] = useState<string>((): string => {
    return range ? formatAny(type, range[1]) : '';
  });
  const [localRange, setLocalRange] = useState<Range | null>(range);

  // 外部から範囲が変更されたときにローカルの状態も更新する
  useEffect((): void => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocalRange(range);
    setMinValue(range ? formatAny(type, range[0]) : '');
    setMaxValue(range ? formatAny(type, range[1]) : '');
  }, [range, type]);

  return (
    <Container>
      <span>{t('filter')}</span>
      <Input
        name="left"
        type="text"
        inputMode="numeric"
        style={{ width: '80px', padding: '4px', textAlign: 'center' }}
        value={minValue}
        placeholder={
          type === 'time'
            ? 'HH:MM'
            : type === 'duration'
              ? 'XhYm'
              : type === 'distance'
                ? 'XX.Xkm'
                : ''
        }
        status={
          localRange !== null &&
          (Number.isNaN(localRange[0]) || localRange[1] <= localRange[0])
            ? 'error'
            : ''
        }
        onChange={(event: ChangeEvent<HTMLInputElement>): void => {
          const replacer =
            type === 'time'
              ? TIME_REPLACER
              : type === 'duration'
                ? DURATION_REPLACER
                : type === 'distance'
                  ? DISTANCE_REPLACER
                  : COUNT_REPLACER;
          const value = event.target.value.replace(replacer, '');

          setMinValue(value);

          const parsedValue = parseAny(type, value);
          if (value === '') {
            // 入力値が空白になったときはデータの最小値を範囲の下限にする
            // ただし最小値と最大値をフィルターにするのは無意味であるため、そのときはフィルターを解除する
            cancel1();
            setLocalRange((currentRange: Range | null): Range | null => {
              if (currentRange && currentRange[1] !== max) {
                return [min, currentRange[1]];
              }
              return null;
            });
          } else if (
            parsedValue !== null &&
            value === formatAny(type, parsedValue)
          ) {
            // 入力値が正しい形式であればすぐにフィルターを更新する
            cancel1();
            setLocalRange((currentRange: Range | null): Range => {
              return [parsedValue, currentRange?.[1] ?? max];
            });
          } else {
            // 入力値が正しい形式でないときは、少し待ってユーザーの入力が落ち着いてから、補正した値でフィルターを更新する
            debounce1((): void => {
              if (parsedValue !== null) {
                setLocalRange((currentRange: Range | null): Range => {
                  return [parsedValue, currentRange?.[1] ?? max];
                });
                setMinValue(formatAny(type, parsedValue));
              } else {
                // 補正できなかったときはNaNをセットしてエラー表示にする
                setLocalRange((currentRange: Range | null): Range => {
                  return [NaN, currentRange?.[1] ?? max];
                });
              }
            });
          }
        }}
      />
      <span>~</span>
      <Input
        name="right"
        type="text"
        inputMode="numeric"
        style={{ width: '80px', padding: '4px', textAlign: 'center' }}
        value={maxValue}
        placeholder={
          type === 'time'
            ? 'HH:MM'
            : type === 'duration'
              ? 'XhYm'
              : type === 'distance'
                ? 'XX.Xkm'
                : ''
        }
        status={
          localRange !== null &&
          (Number.isNaN(localRange[1]) || localRange[1] <= localRange[0])
            ? 'error'
            : ''
        }
        onChange={(event: ChangeEvent<HTMLInputElement>): void => {
          const replacer =
            type === 'time'
              ? TIME_REPLACER
              : type === 'duration'
                ? DURATION_REPLACER
                : type === 'distance'
                  ? DISTANCE_REPLACER
                  : COUNT_REPLACER;
          const value = event.target.value.replace(replacer, '');

          setMaxValue(value);

          const parsedValue = parseAny(type, value);
          if (value === '') {
            // 入力値が空白になったときはデータの最大値を範囲の上限にする
            // ただし最小値と最大値をフィルターにするのは無意味であるため、そのときはフィルターを解除する
            cancel2();
            setLocalRange((currentRange: Range | null): Range | null => {
              if (currentRange && currentRange[0] !== min) {
                return [currentRange[0], max];
              }
              return null;
            });
          } else if (
            parsedValue !== null &&
            value === formatAny(type, parsedValue)
          ) {
            // 入力値が正しい形式であればすぐにフィルターを更新する
            cancel2();
            setLocalRange((currentRange: Range | null): Range => {
              return [currentRange?.[0] ?? min, parsedValue];
            });
          } else {
            // 入力値が正しい形式でないときは、少し待ってユーザーの入力が落ち着いてから、補正した値でフィルターを更新する
            debounce2((): void => {
              if (parsedValue !== null) {
                setLocalRange((currentRange: Range | null): Range => {
                  return [currentRange?.[0] ?? min, parsedValue];
                });
                setMaxValue(formatAny(type, parsedValue));
              } else {
                // 補正できなかったときはNaNをセットしてエラー表示にする
                setLocalRange((currentRange: Range | null): Range => {
                  return [currentRange?.[0] ?? min, NaN];
                });
              }
            });
          }
        }}
      />
      <Button
        role="button"
        type="primary"
        disabled={
          !(localRange === null || isValidRange(localRange)) ||
          isSameRange(localRange, range)
        }
        onClick={(): void => {
          if (onDetermine) {
            onDetermine(localRange);
          }
        }}
      >
        {t('apply')}
      </Button>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
`;

export { HistogramFilterInput };
